/**
 * Site Settings Page
 * Customize admin console theme, colors, and appearance
 */

import React, { useState } from 'react';
import { AdminPageLayout } from '@/components/layout/AdminPageLayout';
import { Card } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';
import { Palette, RefreshCw, Check } from 'lucide-react';

export default function SiteSettingsPage() {
  const { theme, setTheme, updateColors, resetToDefault, presetThemes } = useTheme();
  const [showSaved, setShowSaved] = useState(false);

  const handlePresetSelect = (presetTheme: any) => {
    setTheme(presetTheme);
    showSavedMessage();
  };

  const handleColorChange = (colorKey: string, value: string) => {
    updateColors({ [colorKey]: value });
    showSavedMessage();
  };

  const handleReset = () => {
    resetToDefault();
    showSavedMessage();
  };

  const showSavedMessage = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  const colorGroups = [
    {
      title: 'Primary Colors',
      description: 'Main brand colors used for buttons, links, and highlights',
      colors: [
        { key: 'primaryColor', label: 'Primary Color', value: theme.colors.primaryColor },
        { key: 'primaryHoverColor', label: 'Primary Hover Color', value: theme.colors.primaryHoverColor },
      ],
    },
    {
      title: 'Text Colors',
      description: 'Colors used for text throughout the application',
      colors: [
        { key: 'textPrimary', label: 'Primary Text', value: theme.colors.textPrimary },
        { key: 'textSecondary', label: 'Secondary Text', value: theme.colors.textSecondary },
        { key: 'textTertiary', label: 'Tertiary Text', value: theme.colors.textTertiary },
      ],
    },
    {
      title: 'Background Colors',
      description: 'Background colors for page and components',
      colors: [
        { key: 'bgPage', label: 'Page Background', value: theme.colors.bgPage },
        { key: 'bgCard', label: 'Card Background', value: theme.colors.bgCard },
        { key: 'bgHover', label: 'Hover Background', value: theme.colors.bgHover },
        { key: 'borderColor', label: 'Border Color', value: theme.colors.borderColor },
      ],
    },
    {
      title: 'Success Colors',
      description: 'Colors for success states and messages',
      colors: [
        { key: 'successBg', label: 'Background', value: theme.colors.successBg },
        { key: 'successText', label: 'Text', value: theme.colors.successText },
        { key: 'successBorder', label: 'Border', value: theme.colors.successBorder },
      ],
    },
    {
      title: 'Warning Colors',
      description: 'Colors for warning states and messages',
      colors: [
        { key: 'warningBg', label: 'Background', value: theme.colors.warningBg },
        { key: 'warningText', label: 'Text', value: theme.colors.warningText },
        { key: 'warningBorder', label: 'Border', value: theme.colors.warningBorder },
      ],
    },
    {
      title: 'Danger Colors',
      description: 'Colors for error states and messages',
      colors: [
        { key: 'dangerBg', label: 'Background', value: theme.colors.dangerBg },
        { key: 'dangerText', label: 'Text', value: theme.colors.dangerText },
        { key: 'dangerBorder', label: 'Border', value: theme.colors.dangerBorder },
      ],
    },
    {
      title: 'Info Colors',
      description: 'Colors for informational states and messages',
      colors: [
        { key: 'infoBg', label: 'Background', value: theme.colors.infoBg },
        { key: 'infoText', label: 'Text', value: theme.colors.infoText },
        { key: 'infoBorder', label: 'Border', value: theme.colors.infoBorder },
      ],
    },
  ];

  return (
    <AdminPageLayout
      title="🎨 Site Settings"
      subtitle="Customize your admin console theme and appearance"
      actions={
        <>
          {showSaved && (
            <div className="flex items-center gap-2 font-medium animate-fade-in" style={{ color: theme.colors.successText }}>
              <Check className="w-5 h-5" />
              Saved!
            </div>
          )}
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-opacity"
            style={{ backgroundColor: theme.colors.bgHover, color: theme.colors.textPrimary }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            <RefreshCw className="w-4 h-4" />
            Reset to Default
          </button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Current Theme Info */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Palette className="w-6 h-6" style={{ color: theme.colors.textPrimary }} />
            <div>
              <h2 className="admin-heading">Current Theme</h2>
              <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                Active theme: <span className="font-semibold">{theme.name}</span>
              </p>
            </div>
          </div>
        </Card>

        {/* Preset Themes */}
        <Card className="p-6">
          <h2 className="admin-heading mb-4">Preset Themes</h2>
          <p className="text-sm mb-4" style={{ color: theme.colors.textSecondary }}>
            Choose from pre-configured color schemes or customize your own below
          </p>
          <div className="grid grid-cols-4 gap-4">
            {presetThemes.map((preset) => (
              <button
                key={preset.name}
                onClick={() => handlePresetSelect(preset)}
                className="p-4 border-2 rounded-lg transition-all hover:shadow-md"
                style={{
                  borderColor: theme.name === preset.name ? theme.colors.primaryColor : theme.colors.borderColor,
                  backgroundColor: theme.name === preset.name ? theme.colors.primaryColor + '10' : theme.colors.bgCard
                }}
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="flex gap-2">
                    <div
                      className="w-8 h-8 rounded-full shadow-sm"
                      style={{ backgroundColor: preset.colors.primaryColor }}
                    />
                    <div
                      className="w-8 h-8 rounded-full shadow-sm"
                      style={{ backgroundColor: preset.colors.successBg }}
                    />
                    <div
                      className="w-8 h-8 rounded-full shadow-sm"
                      style={{ backgroundColor: preset.colors.warningBg }}
                    />
                  </div>
                  <span className="text-sm font-semibold" style={{ color: theme.colors.textPrimary }}>{preset.name}</span>
                  {theme.name === preset.name && (
                    <span className="text-xs font-medium" style={{ color: theme.colors.primaryColor }}>Active</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Color Customization */}
        <div className="space-y-4">
          {colorGroups.map((group) => (
            <Card key={group.title} className="p-6">
              <h3 className="admin-subheading mb-2">{group.title}</h3>
              <p className="text-sm mb-4" style={{ color: theme.colors.textSecondary }}>{group.description}</p>
              <div className="grid grid-cols-3 gap-4">
                {group.colors.map((color) => (
                  <div key={color.key} className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="admin-label-text mb-1 block">{color.label}</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={color.value}
                          onChange={(e) => handleColorChange(color.key, e.target.value)}
                          className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                        />
                        <input
                          type="text"
                          value={color.value}
                          onChange={(e) => handleColorChange(color.key, e.target.value)}
                          className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono"
                          style={{
                            backgroundColor: theme.colors.bgCard,
                            borderColor: theme.colors.borderColor,
                            color: theme.colors.textPrimary
                          }}
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* Preview Section */}
        <Card className="p-6">
          <h2 className="admin-heading mb-4">Theme Preview</h2>
          <div className="space-y-4">
            {/* Buttons Preview */}
            <div>
              <h3 className="admin-subheading mb-3">Buttons</h3>
              <div className="flex gap-3 flex-wrap">
                <button className="admin-btn-primary">Primary Button</button>
                <button className="admin-btn-secondary">Secondary Button</button>
                <button className="admin-btn-danger">Danger Button</button>
              </div>
            </div>

            {/* Badges Preview */}
            <div>
              <h3 className="admin-subheading mb-3">Badges</h3>
              <div className="flex gap-3 flex-wrap">
                <span className="admin-badge admin-badge-success">Success</span>
                <span className="admin-badge admin-badge-warning">Warning</span>
                <span className="admin-badge admin-badge-danger">Danger</span>
                <span className="admin-badge admin-badge-info">Info</span>
                <span className="admin-badge admin-badge-neutral">Neutral</span>
              </div>
            </div>

            {/* Alerts Preview */}
            <div>
              <h3 className="admin-subheading mb-3">Alerts</h3>
              <div className="space-y-3">
                <div className="admin-alert admin-alert-success">
                  <p className="text-sm font-medium">Success! Your changes have been saved.</p>
                </div>
                <div className="admin-alert admin-alert-warning">
                  <p className="text-sm font-medium">Warning! Please review before proceeding.</p>
                </div>
                <div className="admin-alert admin-alert-danger">
                  <p className="text-sm font-medium">Error! Something went wrong.</p>
                </div>
                <div className="admin-alert admin-alert-info">
                  <p className="text-sm font-medium">Info: Here's some helpful information.</p>
                </div>
              </div>
            </div>

            {/* Form Elements Preview */}
            <div>
              <h3 className="admin-subheading mb-3">Form Elements</h3>
              <div className="space-y-3 max-w-md">
                <div>
                  <label className="admin-label-text mb-1 block">Text Input</label>
                  <input type="text" className="admin-input" placeholder="Enter text..." />
                </div>
                <div>
                  <label className="admin-label-text mb-1 block">Select Dropdown</label>
                  <select className="admin-select">
                    <option>Option 1</option>
                    <option>Option 2</option>
                    <option>Option 3</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="admin-checkbox" />
                    <span className="admin-body-text">Checkbox Option</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </AdminPageLayout>
  );
}
